/**
 * @author tomekbuszewski
 * @since 2019-3-13
 */

import * as React from "react";
import styled, { css } from "styled-components";

import { RATING } from "@config/testIds/rating";

import { transition } from "@ui/helpers";
import { Star as StarIcon } from "@ui/Assets/MyIcons/Star";
import { TestWrapper } from "@ui/Atoms";
import { ITheme } from "@ui/themes/ThemeInterface";

const Star = styled.button`
  background: none;
  padding: 0;
  border: 0;

  & svg {
    transition: ${transition("fill")};
    stroke: ${props => props.theme.palette.blue500};
    fill: transparent;
  }
`;

const StarWrapper = styled.li`
  margin: 0;
`;

/**
 * Rating container
 */
interface RatingProps {
  className?: string;
  count?: number;
  locked?: boolean;
  theme: ITheme;
  onClick: (rating: number) => any;
  value?: number;
  size?: number;
  color?: string;
  withNoMargin?: boolean;
  enableHoverEffect?: boolean;
}
const RatingComponent = ({ count = 5, ...props }: RatingProps) => {
  const array: null[] = new Array(count!).fill(null);

  return (
    <TestWrapper testId={RATING.WRAPPER}>
      <ul className={props.className}>
        {array.map((_, i: number) => {
          const counter = i + 1;
          const isActive = counter <= (props.value || 0);

          return (
            <TestWrapper
              testId={`${RATING.STAR} ${RATING.STAR}-${counter} ${
                isActive
                  ? `${RATING.STAR_FULL} ${RATING.STAR_FULL}-${counter}`
                  : `${RATING.STAR_EMPTY} ${RATING.STAR_EMPTY}-${counter}`
              }`}
              key={`test-rating-star-${i}`}
            >
              <StarWrapper
                key={`rating-star-${i}`}
                onClick={e => {
                  e.preventDefault();
                  props.onClick(counter);
                }}
              >
                <Star type="button">
                  <StarIcon width={props.size ? props.size : 20} />
                </Star>
              </StarWrapper>
            </TestWrapper>
          );
        })}
      </ul>
    </TestWrapper>
  );
};

const fillRating = (count: number, color: string): string => {
  return Array(count)
    .fill(count)
    .map(
      (item, i) => `
        ${StarWrapper}:nth-of-type(${i + 1}) {
        ${Star} svg {
          stroke: ${color};
          fill: ${color};
        }
      }
  `,
    )
    .join("");
};

const Rating = styled(RatingComponent)<RatingProps>`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${props => (props.withNoMargin ? "0" : props.theme.margins.half)};
  list-style: none;

  font-size: ${props =>
    props.withNoMargin ? null : props.theme.fonts.sizes.h5};
  line-height: 1;

  margin: 0 -${props => props.theme.margins.half};
  padding: 0;

  pointer-events: ${props => props.locked && "none"};

  ${props =>
    props.value &&
    fillRating(
      props.value,
      props.color ? props.color : props.theme.palette.blue500,
    )};

  ${props =>
    props.enableHoverEffect &&
    css`
      &:hover {
        ${StarWrapper} {
          ${Star} svg {
            fill: ${props => props.theme.palette.blue500};
            stroke: ${props => props.theme.palette.blue500};
          }
        }

        ${StarWrapper}:hover ~ ${StarWrapper} {
          ${Star} svg {
            fill: transparent;
          }
        }
      }
    `}
`;

Rating.displayName = "Rating";

export { Rating };
